/**
 * Survey admin operations (contract §6). Routes stay thin: they call these
 * with a repo and turn the Result into an HTTP response.
 */
import { isLockedChange, parseSurveyInput, publishProblems } from "./validate";
import type { ResponseRow, SurveyInput, SurveyRecord, SurveyStatus } from "./types";

export interface Actor {
  id: string;
  email: string;
  role: string;
}

export interface SurveyListItem {
  id: string;
  title: string;
  status: SurveyStatus;
  questionCount: number;
  feedbackEnabled: boolean;
  audience: SurveyInput["audience"];
  shown: number;
  completed: number;
  updatedAt: string;
  publishedAt: string | null;
  startAt: string | null;
  endAt: string | null;
}

export interface SurveyRepo {
  list(): Promise<SurveyListItem[]>;
  get(id: string): Promise<SurveyRecord | null>;
  create(input: SurveyInput, createdBy: string): Promise<SurveyRecord>;
  update(id: string, input: SurveyInput): Promise<SurveyRecord>;
  remove(id: string): Promise<void>;
  setStatus(
    id: string,
    data: { status: SurveyStatus; publishedAt?: Date; cohortFrozenAt?: Date },
  ): Promise<SurveyRecord>;
  deleteResponsesByEmails(surveyId: string, emails: string[]): Promise<number>;
  listResponses(surveyId: string): Promise<ResponseRow[]>;
  logAction(action: string, actorEmail: string, surveyId: string, metadata: unknown): Promise<void>;
}

export type Result<T> =
  | { ok: true; data: T; status?: undefined; error?: undefined; details?: undefined }
  | { ok: false; status: number; error: string; details?: unknown; data?: undefined };

const fail = (status: number, error: string, details?: unknown): Result<never> => ({
  ok: false,
  status,
  error,
  details,
});

/** Owner + admin can change surveys; viewer is read-only. */
export function canEditSurveys(role: string): boolean {
  return role === "owner" || role === "admin";
}

export async function createSurvey(
  repo: SurveyRepo,
  actor: Actor,
  body: unknown,
): Promise<Result<SurveyRecord>> {
  if (!canEditSurveys(actor.role)) return fail(403, "FORBIDDEN");
  const parsed = parseSurveyInput(body);
  if (!parsed.ok) return fail(400, "VALIDATION_ERROR", { errors: parsed.errors });
  return { ok: true, data: await repo.create(parsed.value, actor.id) };
}

export async function updateSurvey(
  repo: SurveyRepo,
  actor: Actor,
  id: string,
  body: unknown,
): Promise<Result<SurveyRecord>> {
  if (!canEditSurveys(actor.role)) return fail(403, "FORBIDDEN");
  const parsed = parseSurveyInput(body);
  if (!parsed.ok) return fail(400, "VALIDATION_ERROR", { errors: parsed.errors });
  const existing = await repo.get(id);
  if (!existing) return fail(404, "SURVEY_NOT_FOUND");
  if (existing.status === "ended") return fail(409, "SURVEY_ENDED");
  if (isLockedChange(existing.status, existing.questions, parsed.value.questions)) {
    return fail(409, "SURVEY_LOCKED");
  }
  return { ok: true, data: await repo.update(id, parsed.value) };
}

export async function publishSurvey(
  repo: SurveyRepo,
  actor: Actor,
  id: string,
  now: Date,
): Promise<Result<SurveyRecord>> {
  if (!canEditSurveys(actor.role)) return fail(403, "FORBIDDEN");
  const existing = await repo.get(id);
  if (!existing) return fail(404, "SURVEY_NOT_FOUND");
  if (existing.status !== "draft") return fail(409, "SURVEY_NOT_DRAFT");
  const problems = publishProblems(existing);
  if (problems.length) return fail(400, "SURVEY_NOT_READY", { problems });
  const data = await repo.setStatus(id, { status: "active", publishedAt: now, cohortFrozenAt: now });
  await repo.logAction("survey.publish", actor.email, id, { title: existing.title });
  return { ok: true, data };
}

const TRANSITIONS: Record<SurveyStatus, SurveyStatus[]> = {
  draft: [],
  active: ["paused", "ended"],
  paused: ["active", "ended"],
  ended: [],
};

export async function changeSurveyStatus(
  repo: SurveyRepo,
  actor: Actor,
  id: string,
  target: "paused" | "active" | "ended",
): Promise<Result<SurveyRecord>> {
  if (!canEditSurveys(actor.role)) return fail(403, "FORBIDDEN");
  if (!["paused", "active", "ended"].includes(target)) return fail(400, "INVALID_STATUS");
  const existing = await repo.get(id);
  if (!existing) return fail(404, "SURVEY_NOT_FOUND");
  if (!TRANSITIONS[existing.status].includes(target)) {
    return fail(409, "INVALID_TRANSITION", { from: existing.status, to: target });
  }
  const data = await repo.setStatus(id, { status: target });
  await repo.logAction(`survey.${target === "active" ? "resume" : target === "paused" ? "pause" : "end"}`, actor.email, id, {
    from: existing.status,
  });
  return { ok: true, data };
}

export async function deleteSurvey(
  repo: SurveyRepo,
  actor: Actor,
  id: string,
): Promise<Result<{ deleted: true }>> {
  if (!canEditSurveys(actor.role)) return fail(403, "FORBIDDEN");
  const existing = await repo.get(id);
  if (!existing) return fail(404, "SURVEY_NOT_FOUND");
  if (existing.status !== "draft") return fail(409, "ONLY_DRAFTS_CAN_BE_DELETED");
  await repo.remove(id);
  await repo.logAction("survey.delete", actor.email, id, { title: existing.title });
  return { ok: true, data: { deleted: true } };
}

export async function resetTestResponses(
  repo: SurveyRepo,
  actor: Actor,
  id: string,
): Promise<Result<{ deleted: number }>> {
  if (!canEditSurveys(actor.role)) return fail(403, "FORBIDDEN");
  const existing = await repo.get(id);
  if (!existing) return fail(404, "SURVEY_NOT_FOUND");
  if (existing.testEmails.length === 0) return { ok: true, data: { deleted: 0 } };
  const deleted = await repo.deleteResponsesByEmails(id, existing.testEmails);
  return { ok: true, data: { deleted } };
}
